// AchievementWorkshop - 산업융합촉진 워크숍​

import WorkshopSingle from '@achievement-workshop/components/WorkshopSingle.tsx'
import FilterBox from '@common/components/FilterBox.tsx'
import Pagination from '@common/components/Pagination.tsx'
import Search from '@common/components/Search'
import {Checkbox} from '@common/components/form'
import {useScrollToTop} from '@common/hooks/useScrollToTop'
import API from '@common/libs/api.ts'
import {addQueryArg, extractPgParams, initialPgParams, removeQueryArg} from '@common/libs/helper.ts'
import queryKeys from '@common/libs/query-keys.ts'
import {type ItemsResponse, type PgParams, type AchievementWorkshop as Workshop} from '@common/types'
import {useQuery} from '@tanstack/react-query'
import {Fragment, useEffect, useState} from 'react'
import {Case, Switch} from 'react-if'
import {useLocation, useNavigate} from 'react-router-dom'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { BrowserView, MobileView } from 'react-device-detect'
import useThrottle from '@common/hooks/useThrottle'

const currentYear = new Date().getFullYear()

export type Props = {
    all_workshop_fields: {[key: string]: string}
    base_url: string
    endpoint: string
    nonce: string
    num_years: string
}

export default function AchievementWorkshop(props: Props) {
    const allWorkshopFields = props.all_workshop_fields
    const baseUrl = props.base_url
    const numYears = parseInt(props.num_years)
    const navigate = useNavigate()
    const location = useLocation()

    // TODO: 이 페이지네이션 기능을 Hook으로 분리해도 좋을것 같다.
    const qs = new URLSearchParams(location.search)
    const pg = Math.max(1, parseInt(qs.get('pg') ?? '0'))
    const fields = (qs.get('fields') ?? '').split(',').filter((f) => f.length > 0)
    const kwd = qs.get('kwd') ?? ''
    const years = (qs.get('years') ?? '')
        .split(',')
        .filter((y) => y.length > 0)
        .map((y) => parseInt(y))

    const [pgParams, setPgParams] = useState<PgParams>({
        ...initialPgParams(),
        page: pg,
    })

    useEffect(() => {
        setPgParams((prev) => ({...prev, page: pg}))
    }, [pg])

    const {
        error,
        isError,
        isFetching,
        isSuccess,
        data: workshops,
    } = useQuery({
        queryFn: async () => {
            const response = await API.Workshop.query({
                fields,
                kwd,
                pg,
                years,
            })
            setPgParams(extractPgParams(response))
            return (response.data.data as ItemsResponse<Workshop>).items
        },
        queryKey: queryKeys.achievementWorkshopQuery(pg, fields, kwd, years),
    })

    useScrollToTop()

    const handlePageChange = useThrottle((page: number) => {
        navigate(
          addQueryArg(
            {
              pg: page.toString(),
            },
            location,
          ),
        );
      }, 300);

    return (
        <div
            className="achievement-workshop"
            id="achievement-workshop"
        >
            <div className="mb-32px">
                <FilterBox>
                    <FilterBox.Row>
                        <FilterBox.CheckboxListLabel>분야</FilterBox.CheckboxListLabel>
                        <FilterBox.CheckboxList>
                            <FilterBox.CheckboxListItem>
                                <Checkbox
                                    checked={fields.length === 0}
                                    id="workshop_field-all"
                                    label="전체"
                                    name="workshop_field[]"
                                    type="checkbox"
                                    value="all"
                                    onChange={() => {
                                        navigate(removeQueryArg('fields', location))
                                    }}
                                />
                            </FilterBox.CheckboxListItem>
                            {Object.entries(allWorkshopFields).map(([key, label]) => (
                                <FilterBox.CheckboxListItem key={key}>
                                    <Checkbox
                                        checked={fields.includes(key)}
                                        id={`workshop_field-${key}`}
                                        label={label}
                                        name="workshop_field[]"
                                        type="checkbox"
                                        value={key}
                                        onChange={(e) => {
                                            const checked = e.target.checked
                                            navigate(
                                                addQueryArg(
                                                    {
                                                        fields: checked
                                                            ? [...fields, key].join(',')
                                                            : fields.filter((field) => key !== field).join(','),
                                                    },
                                                    location,
                                                ),
                                            )
                                        }}
                                    />
                                </FilterBox.CheckboxListItem>
                            ))}
                        </FilterBox.CheckboxList>
                    </FilterBox.Row>
                    <FilterBox.Row>
                        <FilterBox.CheckboxListLabel>검색년도</FilterBox.CheckboxListLabel>
                        <FilterBox.CheckboxList>
                            <FilterBox.CheckboxListItem>
                                <Checkbox
                                    checked={years.length === 0}
                                    id={'workshop_years-all'}
                                    label="전체"
                                    name="workshop_years[]"
                                    type="checkbox"
                                    value="all"
                                    onChange={() => {
                                        navigate(removeQueryArg('years', location))
                                    }}
                                />
                            </FilterBox.CheckboxListItem>
                            {Array(numYears)
                                .fill('')
                                .map((_, index) => {
                                    const year = currentYear - index

                                    return (
                                        <FilterBox.CheckboxListItem key={year}>
                                            <Checkbox
                                                checked={years.includes(year)}
                                                id={`workshop_years-${year}`}
                                                label={year}
                                                name="workshop_years[]"
                                                type="checkbox"
                                                value="all"
                                                onChange={(e) => {
                                                    const checked = e.target.checked
                                                    navigate(
                                                        addQueryArg(
                                                            {
                                                                years: checked
                                                                    ? [...years, year].join(',')
                                                                    : years.filter((y) => year !== y).join(','),
                                                            },
                                                            location,
                                                        ),
                                                    )
                                                }}
                                            />
                                        </FilterBox.CheckboxListItem>
                                    )
                                })}
                        </FilterBox.CheckboxList>
                    </FilterBox.Row>
                </FilterBox>
            </div>
            <div className="search-container mb-8px flex flex-col gap-12px lg:flex lg:flex-row-reverse lg:items-center lg:justify-between">
                <Search
                    defaultValue={kwd}
                    id="kwd"
                    name="kws"
                    placeholder="검색어를 입력해주세요"
                    onSearch={(value) => {
                        navigate(addQueryArg({kwd: value}, location))
                    }}
                />
                <div className="title flex justify-start text-160 lg:mb-0px lg:text-160">
                    {fields.length > 0 && (
                        <span>
                            <span className="text-blue-main font-700 mr-4px">
                                {fields.map((field) => allWorkshopFields[field]).join(', ')}
                            </span>
                            {' 분야 옴부즈만 워크숍'}
                        </span>
                    )}
                </div>
            </div>
            <div className="search-result-count mb-4px !invisible">
                총 <span className="text-blue-main">{pgParams.total_items}</span>건
            </div>
            <div className="hidden lg:block lg:divider lg:h-4px lg:bg-black" />
            <div className="workshop-list-container">
                <Switch>
                    <Case condition={isError}>
                        <div className="error">에러! {error?.message}</div>
                    </Case>
                    <Case condition={isFetching}>
                        <div>
                            <MobileView>
                                <Skeleton count={5} height={147} />
                            </MobileView>
                            <BrowserView>
                                <Skeleton count={5} height={134} />
                            </BrowserView>
                            <div className="pagination-container">
                                <Pagination
                                    totalPages={pgParams.total_pages}
                                    current={pgParams.page}
                                    onChange={(page) => handlePageChange(page)}
                                />
                            </div>
                        </div>
                    </Case>
                    <Case condition={isSuccess && !!workshops && !workshops.length}>
                        <div className="no-data">검색된 워크숍 항목이 없습니다.</div>
                    </Case>
                    <Case condition={isSuccess && !!workshops && workshops.length > 0}>
                        {!!workshops && (
                            <Fragment>
                                <ul className="workshop-list grid grid-cols-1 list-none p-0px ml-0px mt-0px mr-0px gap-y-10px mb-40px">
                                    {workshops.map((item) => (
                                        <WorkshopSingle
                                            allWorkshopFields={allWorkshopFields}
                                            key={item.id}
                                            baseUrl={baseUrl}
                                            item={item}
                                        />
                                    ))}
                                </ul>
                                <div className="pagination-container">
                                    <Pagination
                                        totalPages={pgParams.total_pages}
                                        current={pgParams.page}
                                        onChange={(page) => handlePageChange(page)}
                                    />
                                </div>
                            </Fragment>
                        )}
                    </Case>
                </Switch>
            </div>
        </div>
    )
}
